
import Vue from 'vue';
import { mapState } from 'vuex';
import throttle from '@/components/utils/throttle';
import type { IPost } from '@/types/community';

const VISIBILITY_THRESHOLD = 0.5;
const POST_PREVIEW_CLASS = 'war-room-post-preview-presenter';
const POST_PREVIEW_SELECTOR = `.${POST_PREVIEW_CLASS}`;
const PINNED_POST_PREVIEW_SELECTOR = '.war-room-pinned-post';
const TIME_IN_VIEWPORT_THRESHOLD = 1000;
const PRESONALIZED_ALOGORITHM_NAME = 'Personalized';

interface IData {
  observer: null | IntersectionObserver;
  mutationObserver: null | MutationObserver;
  postsWereInViewPort: Record<string, boolean>;
}

interface IPostParams {
  post_days_look_back?: Number | null;
  output_user_id?: Number | null;
  scores?: number[];
  feed_algo?: string;
  idWasInViewport?: number[];
}

export default Vue.extend({
  name: 'WarRoomPostListObserver',
  data(): IData {
    return {
      observer: null,
      mutationObserver: null,
      postsWereInViewPort: {},
    };
  },
  computed: {
    ...mapState('warRoom', ['post_dict']),
  },
  watch: {
    postsWereInViewPort: {
      deep: true,
      immediate: true,
      handler: throttle(function () {
        // @ts-ignore
        this.sendEvent();
      }, TIME_IN_VIEWPORT_THRESHOLD),
    },
  },
  mounted() {
    if (!window.IntersectionObserver) return;

    this.initIntersectionObserver();
    this.initMutationObserver();

    const initialPostList = this.$el.querySelectorAll(`${POST_PREVIEW_SELECTOR}, ${PINNED_POST_PREVIEW_SELECTOR}`);

    this.observePosts(Array.from(initialPostList));
  },
  beforeDestroy() {
    this.observer?.disconnect();
    this.mutationObserver?.disconnect();
  },
  methods: {
    initIntersectionObserver(): void {
      this.observer = new IntersectionObserver(this.onEntry, {
        threshold: VISIBILITY_THRESHOLD,
      });
    },
    initMutationObserver(): void {
      this.mutationObserver = new MutationObserver(this.onMutation);

      this.mutationObserver.observe(this.$el, {
        childList: true,
        subtree: true,
      });
    },
    observePosts(posts: Array<HTMLElement | Element>) {
      posts.forEach(el => this.observer?.observe(el));
    },
    onEntry(entry: IntersectionObserverEntry[]) {
      entry.forEach(({ isIntersecting, target }) => {
        const {
          dataset: { postSlug },
        } = target as HTMLElement;

        if (!postSlug) return;

        Vue.set(this.postsWereInViewPort, postSlug, isIntersecting);
      });
    },
    onMutation(mutations: MutationRecord[]) {
      mutations.forEach(({ addedNodes }) => {
        addedNodes.forEach(node => {
          const htmlNode = node as HTMLElement;
          if (htmlNode.localName === 'div' && htmlNode.classList.contains(POST_PREVIEW_CLASS)) {
            this.observePosts([htmlNode]);
          }
        });
      });
    },
    sendEvent(): void {
      const currentSlugs = Object.keys(this.postsWereInViewPort);
      if (!currentSlugs.length) return;

      const firstNonPinnedPost = Object.values(this.post_dict as Record<string, IPost>).find(
        ({ pinned, slug }: IPost) => !pinned && currentSlugs.includes(slug),
      );
      if (!firstNonPinnedPost) return;

      const scores: number[] = [];
      const postsInViewport: number[] = [];
      const postParams: IPostParams = {};

      const isPersonalized = firstNonPinnedPost.algorithm_metadata?.algorithm_name === PRESONALIZED_ALOGORITHM_NAME;

      Object.entries(this.postsWereInViewPort).forEach(([postSlug, isIntersecting]) => {
        if (!isIntersecting) return;

        const post = this.post_dict[postSlug];

        postsInViewport.push(post.id);

        if (isPersonalized) {
          const score = isNaN(post.algorithm_metadata?.score) ? -1 : post.algorithm_metadata?.score;
          scores.push(score);
        }
      });

      postParams.feed_algo = firstNonPinnedPost.algorithm_metadata?.algorithm_name;
      postParams.idWasInViewport = postsInViewport;

      if (isPersonalized) {
        postParams.post_days_look_back = firstNonPinnedPost.algorithm_metadata?.post_days_look_back;
        postParams.output_user_id = firstNonPinnedPost.algorithm_metadata?.output_user_id;
        postParams.scores = scores;
      }

      this.$analytics.logAhoyEvent('feed__posts__observe', { ...postParams });
    },
  },
});
